import type React from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { useIntl } from 'react-intl';
import { useAppState } from 'state';
import { useQuery, useMutation } from '@apollo/client';

import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import Typography from 'app/ui/components/atoms/typography';
import { PillFieldSet, type Pills } from 'app/ui/components/molecules/pill-field-set';

import PageTitle from 'app/ui/components/molecules/page-title';
import EditProfile from 'app/ui/components/molecules/edit-profile';
import ViewProfile from 'app/ui/components/molecules/view-profile';
import GenericPage from 'app/ui/components/layouts/generic-page';
import WorkoutList from 'app/ui/components/molecules/workout-list';

import { ProfilePage as PROFILE } from 'app/pages/profile/profile.gql';
import { PatchUser as PATCH_USER } from 'app/pages/profile/patch-user.gql';
import { PatchGym as PATCH_GYM } from 'app/pages/profile/patch-gym.gql';
import { canPerform } from 'app/features/leads/utils/can-perform-partner-permission';
import type {
  Equipment,
  PatchUserMutation,
  PatchUserMutationVariables,
  PatchGymMutation,
  PatchGymMutationVariables,
  ProfilePageQuery,
  ProfilePageQueryVariables,
} from 'app/types/graphql';

import { lessonToWorkout } from 'app/utils/workouts';

export type UserFormData = {
  id: number;
  firstName: string;
  lastName: string;
  password: string;
  confirmedPassword: string;
};

export type GymFormData = {
  equipment: Equipment[];
};

type ProfileWithUserProps = {
  userId: number;
};

type ProfilePageWrapperProps = {
  userId: number;
  profileData: ProfilePageQuery;
};

enum MyLibraryType {
  SAVED = 'SAVED',
  COMPLETE = 'COMPLETE',
}

const WorkoutListWrapper = styled.div`
  margin-top: 1.5rem;
`;

const MyLibraryHeader = styled(Typography)`
  margin-top: 5rem;
`;

const ProfilePageWrapper: React.FC<ProfilePageWrapperProps> = ({
  userId,
  profileData: { userById, allEquipment, myPartners },
}) => {
  const [editMode, setEditMode] = useState(false);
  const [myLibraryVisible, setMyLibraryVisible] = useState<MyLibraryType>(MyLibraryType.SAVED);

  const intl = useIntl();

  const initialUserFormData: UserFormData = {
    id: userId,
    firstName: userById?.firstName || '',
    lastName: userById?.lastName || '',
    password: '',
    confirmedPassword: '',
  };

  const initialGymFormData: GymFormData = {
    equipment: userById?.gym?.equipment as Equipment[],
  };

  const canEditGym = canPerform(
    userById?.partnerPermissions ?? [],
    'Gym',
    'update',
    myPartners?.currentPartnerId ?? null,
  );

  const [userDetails, setUserDetails] = useState<UserFormData>(initialUserFormData);
  const [gymDetails, setGymDetails] = useState<GymFormData>(initialGymFormData);

  const [isPatchLoading, setIsPatchLoading] = useState<boolean>(false);
  const [isPatchError, setIsPatchError] = useState<boolean>(false);

  const [formErrorMessage, setFormErrorMessage] = useState<string>('');

  const [patchUserById] = useMutation<PatchUserMutation, PatchUserMutationVariables>(PATCH_USER, {
    onCompleted: () => {
      setUserDetails({ ...userDetails, password: '', confirmedPassword: '' });
    },
  });

  const [patchGymById] = useMutation<PatchGymMutation, PatchGymMutationVariables>(PATCH_GYM, {
    onCompleted: () => {
      setIsPatchLoading(false);
      setFormErrorMessage('');
    },
    refetchQueries: ['ProfilePage'],
  });

  const handleEditButtonClick = () => {
    setEditMode(true);
  };

  const handleCancelButtonClick = () => {
    setUserDetails({ ...initialUserFormData });
    setEditMode(false);
  };

  const handleSaveProfileButtonClick = async () => {
    if (userDetails.password.length > 0 && userDetails.password !== userDetails.confirmedPassword) {
      const matchingPasswordsMessage = intl.formatMessage({ id: 'profile.messages.passwords.matching' });
      return setFormErrorMessage(matchingPasswordsMessage);
    }

    setIsPatchLoading(true);

    const { confirmedPassword, id, ...validUserDetails } = userDetails;

    const gymEquipmentIds = gymDetails.equipment.map((equipment) => ({ id: equipment.id }));

    try {
      await patchUserById({ variables: { id: userId, input: validUserDetails } });
      if (canEditGym && userById?.gym?.id) {
        const gymInput = { equipment: gymEquipmentIds };
        await patchGymById({ variables: { id: userById.gym.id, input: gymInput } });
      }
      setIsPatchLoading(false);
      setFormErrorMessage('');
      setEditMode(false);
    } catch (_error) {
      setIsPatchLoading(false);
      setIsPatchError(true);
    }
  };

  if (isPatchLoading) {
    return <Loading />;
  }

  if (isPatchError) {
    return <ErrorComponent />;
  }

  const savedWorkouts = userById?.savedLessons?.map((lesson) => lessonToWorkout(lesson)) || [];
  const completedWorkouts = userById?.completedLessons?.map((lesson) => lessonToWorkout(lesson)) || [];

  const myLibraryPills: Pills = Object.values(MyLibraryType).map((type) => ({
    id: type,
    name: intl.formatMessage({ id: `myLibrary.labels.${type.toLowerCase()}` }),
    isChecked: myLibraryVisible === type,
  }));

  const profileHeader = intl.formatMessage({ id: 'profile.header' });
  const myLibraryHeader = intl.formatMessage({ id: 'myLibrary.header' });

  return (
    <GenericPage pageTitle="Profile">
      <PageTitle>
        <Typography variant="h2">{profileHeader}</Typography>
      </PageTitle>
      {editMode ? (
        <EditProfile
          gymDetails={gymDetails}
          userDetails={userDetails}
          allEquipment={allEquipment as Equipment[]}
          handleCancelButtonClick={handleCancelButtonClick}
          handleSaveProfileButtonClick={handleSaveProfileButtonClick}
          handleUserDetailsChange={setUserDetails}
          handleGymDetailsChange={setGymDetails}
          errorMessage={formErrorMessage}
          canEditGym={canEditGym}
        />
      ) : (
        <>
          <ViewProfile handleEditButtonClick={handleEditButtonClick} userDetails={userById} />
          <MyLibraryHeader variant="h2">{myLibraryHeader}</MyLibraryHeader>
          <PillFieldSet
            label=""
            pills={myLibraryPills}
            onChange={(event) => setMyLibraryVisible(event.target.value as MyLibraryType)}
          />
          <WorkoutListWrapper>
            <WorkoutList workouts={myLibraryVisible === MyLibraryType.SAVED ? savedWorkouts : completedWorkouts} />
          </WorkoutListWrapper>
        </>
      )}
    </GenericPage>
  );
};

const ProfileWithUser: React.FC<ProfileWithUserProps> = ({ userId }) => {
  const { data, loading, error } = useQuery<ProfilePageQuery, ProfilePageQueryVariables>(PROFILE, {
    variables: { id: userId },
    fetchPolicy: 'no-cache',
  });

  if (loading) {
    return <Loading />;
  }

  if (error || !data || !data.userById || !data.allEquipment) {
    return <ErrorComponent />;
  }

  const { userById, allEquipment, myPartners } = data;

  return <ProfilePageWrapper profileData={{ userById, allEquipment, myPartners }} userId={userId} />;
};

const Profile: React.FC = () => {
  const userId = useAppState(({ auth }) => auth.userId);

  if (!userId) {
    return <ErrorComponent />;
  }

  return <ProfileWithUser userId={userId} />;
};

export default Profile;
